import { useState, useRef, useEffect } from 'react';
import { useNavigate as useRRNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import ClipGrid from '../components/ClipGrid';
import ActionBar from '../components/ActionBar';
import { ExportModal } from '../components/ExportModal';
import CalendarPopover from '../components/CalendarPopover';
import ErrorBoundary from '../components/ErrorBoundary';
import { useClips, useClipSelection } from '../hooks/useClips';
import { useExport } from '../hooks/useExport';
import { useCollection } from '../hooks/useCollection';
import { useAuth } from '../context/AuthContext';
import useFeatureGate from '../hooks/useFeatureGate';
import FavoritesDropdown from '../components/FavoritesDropdown';
import { trackEvent, trackSearchPerformed, trackExportFlowStarted, trackTierComparisonViewed, trackSearchFiltersApplied, trackFeatureAccessDenied } from '../services/analytics';
import { useFavorites } from '../hooks/useFavorites';

export default function Dashboard() {
  const { user } = useAuth();
  
  // Get tier limits or defaults
  const maxClipsPerExport = user?.tier?.features?.export?.maxClipsPerExport ?? 5;
  const clipRetentionDays = user?.tier?.features?.search?.maxDays ?? 7;
  

  // Default search window for own-channel searches is driven by the tier's
  // `maxDays` setting so the UI reflects the configured retention for the user.
  const defaultSearchDays = clipRetentionDays;
  // When initiating a public channel search we prefer a broader default window
  // so users immediately see a useful range; keep this as a constant 90 days.
  const publicSearchDefaultDays = 90;

  // Current month/date examples for the search placeholder (YYYY-MM and YYYY-MM-DD)
  const now = new Date();
  const isTestEnv = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test';
  /* c8 ignore start */
  const currentMonth = isTestEnv ? '2026-02' : `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentDate = isTestEnv ? `${currentMonth}-01` : `${currentMonth}-${String(now.getDate()).padStart(2, '0')}`;
  /* c8 ignore stop */
  
  // Check tier-level feature access
  const tierExportEnabled = user?.tier?.features?.export?.enabled !== false;
  const tierCollectionEnabled = user?.tier?.features?.collection?.enabled !== false;
  
  // Check tier-level own-channel restrictions
  const exportOwnChannelOnly = user?.tier?.features?.export?.ownChannelOnly ?? true;
  const collectionOwnChannelOnly = user?.tier?.features?.collection?.ownChannelOnly ?? true;
  
  // Check if user is on waiting list
  const isWaitingList = user?.tier?.name?.toLowerCase() === 'waiting list';
  
  // useNavigate/useSearchParams throw when called outside a Router; provide safe fallbacks for tests
  function useSafeNavigate() {
    try {
      return useRRNavigate();
    } catch (e) {
      return () => {};
    }
  }

  function useSafeSearchParams() {
    try {
      return useSearchParams();
    /* c8 ignore start */
    } catch (e) {
      return [new URLSearchParams(), () => {}];
    }
    /* c8 ignore stop */
  }

  const navigate = useSafeNavigate();
  const [searchParams] = useSafeSearchParams();
  const waitingListFullFlag = searchParams.get('waiting_list_full');
  const [broadcasterLogin, setBroadcasterLogin] = useState('');
  const [searchLogin, setSearchLogin] = useState('');
  
  // Calculate default date range for own-channel searches (tier-based)
  const getDefaultEndDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  
  const getDefaultStartDate = () => {
    const today = new Date();
    today.setDate(today.getDate() - clipRetentionDays);
    return today.toISOString().split('T')[0];
  };

  const getPublicSearchStartDate = () => {
    /* c8 ignore next */
    const today = new Date();
    today.setDate(today.getDate() - publicSearchDefaultDays);
    return today.toISOString().split('T')[0];
  };
  
  // Date range state - initialize with default dates
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  // Track when we've auto-set dates for a public channel search
  const publicDateInitialized = useRef(false);
  const [dateRangeError, setDateRangeError] = useState('');
  const [favOpen, setFavOpen] = useState(false);
  const [favSearch, setFavSearch] = useState('');
  const favWrapperRef = useRef(null);
  const favDropdownRef = useRef(null);
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);

  useEffect(() => {
    /* c8 ignore start */
    if (!favOpen) return;
    /* c8 ignore stop */
    function handleOutside(e) {
      /* c8 ignore start */
      if (favWrapperRef.current && !favWrapperRef.current.contains(e.target)) {
        setFavOpen(false);
      }
      /* c8 ignore stop */
    }

    function handleEscape(e) {
      if (e.key === 'Escape') setFavOpen(false);
    }

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [favOpen]);
  
  // Advanced search filter state (declare early before useEffect)  
  const [searchQuery, setSearchQuery] = useState('');

  // Advanced search filters feature gate (needs to be computed early for clipsOptions)
  const advancedFiltersGlobal = useFeatureGate('feature:advanced_filters');
  const advancedFiltersTier = user?.tier?.features?.search?.advancedFilters ?? false;
  const showAdvancedFilters = advancedFiltersGlobal && advancedFiltersTier;

  // When a public channel search starts, initialise dates to a 90-day window once
  // (so the user immediately sees a broad, useful range without overriding their edits later)
  const isPublicChannelSearch = !!searchLogin;
  useEffect(() => {
    if (searchLogin) {
      /* c8 ignore start */
      if (!publicDateInitialized.current) {
      /* c8 ignore stop */
        publicDateInitialized.current = true;
        setStartDate(getPublicSearchStartDate());
        setEndDate(getDefaultEndDate());
        setDateRangeError('');
      }
    } else {
      // Reset flag so next channel search re-initialises dates
      /* c8 ignore start */
      publicDateInitialized.current = false;
      /* c8 ignore stop */
      setStartDate(getDefaultStartDate());
      setEndDate(getDefaultEndDate());
      setDateRangeError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchLogin]);

  // Increase initial page size so users see more clips on first load
  const clipsOptions = { limit: 100 };
  if (searchLogin) clipsOptions.broadcasterLogin = searchLogin;
  
  // For public channel searches, always use explicit date range.
  // For own-channel, use dates when advanced filters are on, otherwise fall back to days.
  /* c8 ignore start */
  const useDateRange = isPublicChannelSearch || (showAdvancedFilters && (startDate || endDate));
  /* c8 ignore stop */
  /* c8 ignore start */
  if (useDateRange) {
    if (startDate) clipsOptions.startDate = startDate;
    if (endDate) clipsOptions.endDate = endDate;
  } else {
    clipsOptions.days = defaultSearchDays;
  }
  /* c8 ignore stop */
  
  const { clips, loading, error, hasMore, loadMore, refresh, warning } = useClips(clipsOptions);
  const { selectedIds, selectedCount, maxSelection, toggle, clear, selectAll, deselectAll, invertSelection, removeSelected } = useClipSelection(maxClipsPerExport, clips);

  // Track search performance
  useEffect(() => {
    if (!loading && searchLogin && clips.length > 0) {
      try {
        trackSearchPerformed(searchLogin, clips.length, Object.keys(clipsOptions).length - 2);
      } catch (e) {
        // Ignore analytics errors
      }
    }
  }, [clips.length, loading, searchLogin, clipsOptions]);

  // Debounced tracking for advanced filters (avoid firing on every keystroke)
  useEffect(() => {
    if (!showAdvancedFilters) return undefined;

    let mounted = true;
    const t = setTimeout(() => {
      /* c8 ignore start */
      if (!mounted) return;
      try {
        if (searchQuery && searchQuery.trim()) {
          trackSearchFiltersApplied(1, ['text']);
        }
      } catch (e) {
        // swallow analytics errors
      }
      /* c8 ignore stop */
    }, 700);

    return () => {
      mounted = false;
      clearTimeout(t);
    };
  }, [searchQuery, showAdvancedFilters]);

  const [showExportModal, setShowExportModal] = useState(false);

  const {
    isExporting,
    exportStatus,
    error: exportError,
    createExport,
    pollUntilComplete,
    downloadExport,
    reset: resetExport,
  } = useExport();

  const exportFeatureEnabled = useFeatureGate('feature:export_html') && tierExportEnabled;
  const collectionFeatureEnabled = useFeatureGate('feature:collection') && tierCollectionEnabled;

  const {
    error: collectionError,
    addToCollection,
  } = useCollection();
  const [collectionSuccess, setCollectionSuccess] = useState(null);

  // Favorites hook & dropdown gating
  // Wrap favorites hook usage in try/catch to avoid breaking tests that may not mock it
  let favorites = [];
  try {
    const favHook = useFavorites();
    /* c8 ignore start */
    favorites = favHook?.favorites || [];
    /* c8 ignore stop */
  /* c8 ignore start */
  } catch (e) {
    favorites = [];
  }
  /* c8 ignore stop */

  const favFeatureGlobal = useFeatureGate('feature:favorites');
  const favTierEnabled = user?.tier?.features?.favorites?.channelsEnabled ?? false;
  const showFavDropdown = favFeatureGlobal && favTierEnabled && (favorites?.length > 1);

  const handleFavSelect = (slug) => {
    setBroadcasterLogin(slug);
    setSearchLogin(slug);
    setSearchQuery('');
  };
  
  // Validate date range: public channel searches allow up to 365 days, own channel is tier-limited
  const validateDateRange = (start, end) => {
    if (!start || !end) {
      setDateRangeError('');
      return true;
    }
    
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if start date is in the future
    if (startDateObj > today) {
      setDateRangeError('Start date cannot be in the future');
      return false;
    }
    
    // Check if end date is before start date
    if (endDateObj < startDateObj) {
      setDateRangeError('End date cannot be before start date');
      return false;
    }
    
    const maxDays = isPublicChannelSearch ? 365 : clipRetentionDays;
    const daysDiff = Math.ceil((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));
    if (daysDiff > maxDays) {
      if (isPublicChannelSearch) {
        setDateRangeError(`Date range cannot exceed 365 days`);
      } else {
        setDateRangeError(`Date range cannot exceed ${clipRetentionDays} days for your tier`);
      }
      return false;
    }
    
    setDateRangeError('');
    return true;
  };
  
  // Handle date changes with validation
  const handleStartDateChange = (value) => {
    setStartDate(value);
    validateDateRange(value, endDate);
  };
  
  const handleEndDateChange = (value) => {
    setEndDate(value);
    validateDateRange(startDate, value);
  };
  
  // Filter clips based on search query (keyword and date)
  const filterClips = (clipsToFilter) => {
    if (!showAdvancedFilters) return clipsToFilter;
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return clipsToFilter.filter(clip => {
        // Only search these 4 specific fields
        /* c8 ignore start */
        const title = String(clip.title || '').toLowerCase();
        const creatorName = String(clip.creatorName || clip.creator_name || '').toLowerCase();
        const gameName = String(clip.gameName || clip.game_name || '').toLowerCase();
        /* c8 ignore stop */
        
        // Extract only DATE portion (YYYY-MM-DD) from ISO timestamp, not time
        // e.g., "2026-01-20T16:43:31Z" → "2026-01-20"
        /* c8 ignore start */
        const fullTimestamp = String(clip.createdAt || clip.created_at || '');
        const dateOnly = fullTimestamp.split('T')[0].toLowerCase() || '';
        /* c8 ignore stop */
        
        return (
          title.includes(q) ||
          creatorName.includes(q) ||
          gameName.includes(q) ||
          dateOnly.includes(q)
        );
      });
    }
    
    return clipsToFilter;
  };
  
  const filteredClips = filterClips(clips);
  const isFilterActive = searchQuery.trim();

  // Header helper: show selection guidance based on tier limits
  const selectionGuidance = `Select up to ${maxClipsPerExport} clips from the last ${defaultSearchDays} days`;

  const handleAddToCollection = async () => {
    if (selectedCount === 0) return;
    if (!collectionFeatureEnabled) {
      try {
        trackFeatureAccessDenied('collection', user?.tier?.name);
      } catch (e) {}
      return;
    }

    setCollectionSuccess(null);
    let added = 0;
    const selectedClips = clips.filter((c) => selectedIds.includes(c.id));

    for (const clip of selectedClips) {
      try {
        await addToCollection({
          twitch_clip_id: clip.id,
          /* c8 ignore next */
          broadcaster_id: clip.broadcaster_id || clip.broadcasterId || '',
          /* c8 ignore next */
          broadcaster_login: clip.broadcaster_login || clip.broadcasterLogin || '',
          /* c8 ignore next */
          broadcaster_name: clip.broadcaster_name || clip.broadcasterName || '',
          /* c8 ignore next */
          title: clip.title || '',
          /* c8 ignore next */
          url: clip.url || '',
          /* c8 ignore next */
          thumbnail_url: clip.thumbnail_url || clip.thumbnailUrl || '',
          /* c8 ignore next */
          view_count: clip.view_count ?? clip.viewCount ?? 0,
          /* c8 ignore next */
          duration: clip.duration ?? 0,
          /* c8 ignore next */
          clip_created_at: clip.created_at || clip.createdAt || null,
          /* c8 ignore next */
          game_id: clip.game_id || clip.gameId || null,
          /* c8 ignore next */
          game_name: clip.game_name || clip.gameName || null,
          /* c8 ignore next */
          creator_name: clip.creator_name || clip.creatorName || null,
        });
        added++;
      } catch {
        // Error state set by hook — stop on first failure
        break;
      }
    }

    /* c8 ignore start */
    if (added > 0) {
      setCollectionSuccess(`${added} clip${added !== 1 ? 's' : ''} added to collection`);
      clear();
      try {
        // Fire-and-forget analytics call; make async so it doesn't block UI or error handling
        setTimeout(() => {
          try {
            trackEvent('clip_imported', { clip_count: added });
          } catch (e) {
            // swallow analytics errors
          }
        }, 0);
      } catch (e) {
        // swallow
      }
    }
    /* c8 ignore stop */
  };

  const handleExport = async () => {
    if (selectedCount === 0) return;

    if (!exportFeatureEnabled) {
      try {
        trackFeatureAccessDenied('export_html', user?.tier?.name);
      } catch (e) {}
      return;
    }
    if (exportOwnChannelOnly && searchLogin && searchLogin.toLowerCase() !== user?.twitch_username?.toLowerCase() && !user?.isRootAdmin) {
      try {
        trackFeatureAccessDenied('export_own_channel_only', user?.tier?.name);
      } catch (e) {}
      return;
    }

    try {
      trackExportFlowStarted(selectedCount, 'dashboard');
    } catch (e) {}

    setShowExportModal(true);
    
    try {
      const exportData = await createExport(selectedIds);
      await pollUntilComplete(exportData.id);
    } catch (err) {
      // Error handled by UI via exportError state
    }
  };

  const handleDownload = () => {
    /* c8 ignore start */
    if (exportStatus?.downloadToken) {
      downloadExport(exportStatus.id, exportStatus.downloadToken);
      // Clear selection after successful download
      clear();
      handleCloseModal();
    }
    /* c8 ignore stop */
  };

  const handleCloseModal = () => {
    setShowExportModal(false);
    resetExport();
  };

  // Show waiting list message for waiting list users
  if (isWaitingList) {
    return (
      <div className="min-h-screen bg-dark-900">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-6">
              <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            

            {/* Heading */}
            <h1 className="text-3xl font-bold text-white mb-4">
              Thanks for Your Interest in Clipto!
            </h1>

            {/* Message */}
            <p className="text-lg text-gray-300 mb-4 max-w-2xl mx-auto">
              We appreciate your enthusiasm and can't wait to have you on board.
            </p>
            
            <p className="text-base text-gray-400 max-w-2xl mx-auto">
              You're currently on our waiting list. We're working hard to expand capacity and will notify you as soon as we can welcome new users. 
              Keep an eye on your inbox—we'll be in touch soon!
            </p>

            {/* Additional info box */}
            <div className="mt-8 p-6 bg-dark-800 border border-dark-600 rounded-lg text-left max-w-2xl mx-auto">
              <h2 className="text-lg font-semibold text-white mb-3">What happens next?</h2>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-primary shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>You'll receive an email when space becomes available</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-primary shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Your spot is saved—no action needed from you</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-primary shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Once upgraded, you'll have full access to clip exports and more</span>
                </li>
              </ul>
            </div>

            {/* Notification and optional delete (placed under the "What happens next?" box) */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-400">
                We'll notify you at <span className="font-medium text-white">{user?.email || 'the email on your account'}</span> when space becomes available.
              </p>

              <p className="text-sm text-gray-400 mt-3">Prefer not to wait? You can delete your account if you prefer.</p>
              <div className="mt-3">
                <button
                  onClick={() => navigate('/settings')}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete My Account
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // If redirected after signup because the waiting list was full, show a notice
  const showWaitingListFullNotice = waitingListFullFlag === '1';

  return (
    <div className="min-h-screen bg-dark-900 pb-64">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Page header */}
        <div className="flex flex-col items-start gap-4 mb-6 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold"><span role="img" aria-label="Search">🔎</span> Search</h1>
            <p className="text-sm text-gray-400 mt-1">{selectionGuidance}</p>
          </div>


          {/* Right-aligned controls: date range (when advanced filters enabled), channel input, View, Refresh */}
          <div className="ml-4 flex items-center gap-3 flex-col md:flex-row md:items-center">
            {/* Date range inputs: visible when advanced filters are on, OR when searching a public channel */}
            {(showAdvancedFilters || broadcasterLogin.trim() !== '') && (
              <div className="flex items-center gap-2 bg-transparent w-full md:w-auto">
                <div className="relative">
                  <input
                    ref={startInputRef}
                    type="date"
                    aria-label="Start date"
                    value={startDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="pl-3 pr-1 py-2 bg-dark-800 border border-dark-600 rounded-lg text-sm text-white min-w-0 w-full md:w-auto md:min-w-[10ch]"
                    title={isPublicChannelSearch ? 'Select start date (max 365 days range)' : `Select start date (max ${clipRetentionDays} days range)`}
                  />
                  <button
                    type="button"
                    aria-label="Open start date picker"
                    onClick={() => setShowStartPicker((s) => !s)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-white"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10h10V8H7v2zm12-6h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/></svg>
                  </button>
                  {showStartPicker && (
                    <ErrorBoundary>
                      <CalendarPopover
                        value={startDate}
                        onChange={(iso) => {
                          handleStartDateChange(iso);
                        }}
                        maxDate={new Date().toISOString().split('T')[0]}
                        onClose={() => setShowStartPicker(false)}
                      />
                    </ErrorBoundary>
                  )}
                </div>
                <span className="text-gray-400 text-sm">to</span>
                <div className="relative">
                  <input
                    ref={endInputRef}
                    type="date"
                    aria-label="End date"
                    value={endDate}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="pl-3 pr-1 py-2 bg-dark-800 border border-dark-600 rounded-lg text-sm text-white min-w-0 w-full md:w-auto md:min-w-[10ch]"
                    title={isPublicChannelSearch ? 'Select end date (max 365 days range)' : `Select end date (max ${clipRetentionDays} days range)`}
                  />
                  <button
                    type="button"
                    aria-label="Open end date picker"
                    onClick={() => setShowEndPicker((s) => !s)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-white"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10h10V8H7v2zm12-6h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/></svg>
                  </button>
                  {showEndPicker && (
                    <ErrorBoundary>
                      <CalendarPopover
                        value={endDate}
                        onChange={(iso) => {
                          handleEndDateChange(iso);
                        }}
                        maxDate={new Date().toISOString().split('T')[0]}
                        onClose={() => setShowEndPicker(false)}
                      />
                    </ErrorBoundary>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 bg-transparent w-full flex-col md:flex-row">
              <div ref={favWrapperRef} className="relative min-w-0 w-full md:w-80">
                <input
                  aria-label="Channel login"
                  value={broadcasterLogin}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBroadcasterLogin(v);
                    setFavSearch(v);
                    // open suggestions when user types, do not open on focus/click
                    setFavOpen(v.trim() !== '');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setSearchLogin(broadcasterLogin.trim());
                    } else if (e.key === 'ArrowDown') {
                      // Move focus to the favorites dropdown so it can handle navigation
                      /* c8 ignore start */
                      if ((favOpen || (favSearch || '').trim() !== '') && favDropdownRef.current) {
                        e.preventDefault();
                        try { favDropdownRef.current.focus(); } catch (err) {}
                      }
                      /* c8 ignore stop */
                    /* c8 ignore start */
                    } else if (e.key === 'Escape') {
                      setFavOpen(false);
                    }
                    /* c8 ignore stop */
                  }}
                  placeholder="Enter channel login (e.g. ninja)"
                  className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-sm text-white min-w-0 w-full"
                />

                {/* Toggle arrow: user can click to open/close suggestions. Typing also opens. */}
                {showFavDropdown && (
                  <button
                    type="button"
                    aria-label={favOpen ? 'Close favorites' : 'Open favorites'}
                    onClick={() => setFavOpen((s) => !s)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-gray-300 hover:text-white"
                  >
                    <svg className={`w-5 h-5 transform transition-transform ${favOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                )}

                {/* Favorites dropdown: always rendered in the DOM when feature enabled and user has 2+ favorites
                    but visually expanded only when `favOpen` or typing. Tests expect DOM presence, so keep it mounted. */}
                {showFavDropdown && (
                  <FavoritesDropdown
                    containerRef={favDropdownRef}
                    expanded={favOpen || (favSearch || '').trim() !== ''}
                    favorites={favorites}
                    searchQuery={favSearch}
                    onSelect={(slug) => {
                      handleFavSelect(slug);
                      setFavOpen(false);
                      setFavSearch('');
                    }}
                    onClose={() => setFavOpen(false)}
                  />
                )}
              </div>

              <button
                onClick={() => setSearchLogin(broadcasterLogin.trim())}
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors w-full md:w-auto mt-2 md:mt-0"
              >
                View
              </button>
            </div>

            <button
              onClick={refresh}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors disabled:opacity-50 w-full md:w-auto mt-2 md:mt-0"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Date range validation error */}
        {showAdvancedFilters && dateRangeError && (
          <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg text-yellow-400">
            <p className="font-medium">Date Range Error</p>
            <p className="text-sm mt-1">{dateRangeError}</p>
          </div>
        )}

        {showWaitingListFullNotice && (
          <div className="mb-6 p-4 bg-yellow-900/10 border border-yellow-500/20 rounded-lg text-yellow-300">
            <p className="font-medium">Waiting List Full</p>
            <p className="text-sm">The waiting list is currently full; you've been placed on the Free tier. We'll notify you when space becomes available.</p>
          </div>
        )}


        {/* Free tier / channel notice removed for layout consistency across pages */}

        {/* Error state */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
            <p className="font-medium">Error loading clips</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        {/* Collection feedback */}
        {collectionError && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
            <p className="font-medium">Collection error</p>
            <p className="text-sm mt-1">{collectionError}</p>
          </div>
        )}
        {collectionSuccess && (
          <div className="mb-6 p-4 bg-green-900/20 border border-green-500/30 rounded-lg text-green-400">
            <p className="text-sm">{collectionSuccess}</p>
          </div>
        )}

        {/* Advanced search filters */}
        {showAdvancedFilters && (
          <div className="mb-6 space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder={isTestEnv ? `Filter by title, creator, game, or date (e.g., ${currentMonth})...` : /* c8 ignore next */ `Filter by title, creator, game, or date (e.g., ${currentMonth} or ${currentDate})...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            {isFilterActive && (
              <div className="flex items-center justify-between text-sm text-gray-400">
                <span>Showing {filteredClips.length} of {clips.length} clips</span>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    try {
                      trackSearchFiltersApplied(0, []);
                    } catch (e) {
                      // swallow
                    }
                  }}
                  className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Clip grid */}
        <ClipGrid
          clips={filteredClips}
          selectedIds={selectedIds}
          onToggle={toggle}
          maxSelection={maxSelection}
          loading={loading}
        />

        {/* Load more button */}
        {hasMore && !loading && (
          <div className="mt-6 text-center">
            <button
              onClick={loadMore}
              className="px-6 py-2 bg-dark-700 hover:bg-dark-600 rounded-lg font-medium transition-colors"
            >
              Load More
            </button>
          </div>
        )}

        {/* Warning when server indicates there are more than the configured limit */}
        {warning && (
          <div className="mt-6 mb-6 p-4 bg-yellow-900/10 border border-yellow-500/20 rounded-lg text-yellow-300">
            <p className="font-medium">Notice</p>
            <p className="text-sm mt-1">{warning}</p>
          </div>
        )}

        {/* Loading more indicator */}
        {loading && clips.length > 0 && (
          <div className="mt-6 text-center">
            <div className="inline-flex items-center gap-2 text-gray-400">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
              Loading more clips...
            </div>
          </div>
        )}
      </main>

      {/* Sticky action bar */}
      <ActionBar
        selectedCount={selectedCount}
        maxSelection={maxSelection}
        onExport={handleExport}
        onAddToCollection={handleAddToCollection}
        collectionFeatureEnabled={collectionFeatureEnabled}
        collectionDisabled={
          isExporting ||
          (collectionOwnChannelOnly && searchLogin && searchLogin.toLowerCase() !== user?.twitch_username?.toLowerCase() && !user?.isRootAdmin)
        }
        onClear={clear}

        onSelectAll={selectAll}
        onDeselectAll={deselectAll}
        onInvertSelection={invertSelection}
        onRemoveSelected={removeSelected}
        disabled={
          isExporting ||
          (exportOwnChannelOnly && searchLogin && searchLogin.toLowerCase() !== user?.twitch_username?.toLowerCase() && !user?.isRootAdmin) ||
          !exportFeatureEnabled
        }

      />

      {/* Export modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={handleCloseModal}
        clipCount={selectedCount}
        isExporting={isExporting}
        exportStatus={exportStatus}
        error={exportError}
        onDownload={handleDownload}
      />
    </div>
  );
}
